import React, { useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { ArrowLeft, Dumbbell, X, History, Share2 } from 'lucide-react';
import { RoutineEditor } from './RoutineEditor';
import RoutineShareMenu from '../RoutineShareMenu';

/**
 * Full-screen overlay wrapper for RoutineEditor.
 * Used by all pages that edit routines (MyClientsPage, RoutinesPage, etc.)
 *
 * Props:
 *  - isOpen: boolean
 *  - onClose: () => void
 *  - routine: routine object
 *  - clientUser: user object (the client, or self for personal routines)
 *  - onSave: (editedRoutine) => void
 *  - onSaveAsTemplate?: () => void
 *  - onChangeRoutine?: () => void  — "Change" button callback (omit to hide)
 *  - showNameEditor?: boolean — show routine name editor inside RoutineEditor
 *  - title?: string — custom title override
 *  - subtitle?: string — custom subtitle override
 */
export default function RoutineEditorFullScreen({
  isOpen, onClose, routine, clientUser,
  onSave, onSaveAsTemplate, onChangeRoutine, onShowHistory,
  showNameEditor = true, title, subtitle,
}) {
  const { t } = useTranslation();
  const pushedRef = useRef(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Lock body scroll when open
  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // Push a history entry when opening so browser back button closes the editor
  useEffect(() => {
    if (isOpen && routine && !pushedRef.current) {
      window.history.pushState({ routineEditor: true }, '');
      pushedRef.current = true;
    }
    if (!isOpen) {
      pushedRef.current = false;
    }
  }, [isOpen, routine]);

  const handlePopState = useCallback((e) => {
    if (pushedRef.current) {
      pushedRef.current = false;
      onCloseRef.current?.();
    }
  }, []);

  // Close via history.back() so popstate handler cleans up the pushed entry
  const handleClose = useCallback(() => {
    if (pushedRef.current) {
      window.history.back(); // triggers popstate → onClose
    } else {
      onClose?.();
    }
  }, [onClose]);

  useEffect(() => {
    if (isOpen && routine) {
      window.addEventListener('popstate', handlePopState);
      return () => window.removeEventListener('popstate', handlePopState);
    }
  }, [isOpen, routine, handlePopState]);

  if (!isOpen || !routine) return null;

  const defaultTitle = clientUser?.name
    ? <>{clientUser.name}'s <span className="text-primary">ROUTINE</span></>
    : <><span className="text-primary">ROUTINE</span></>;
  const defaultSubtitle = `${routine?.name || 'Untitled'} · ${routine?.days?.length || 0} days`;

  return (
    <div className="fixed inset-0 z-50 bg-background flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-border/30 flex-shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <button
            onClick={handleClose}
            className="p-1.5 rounded-lg hover:bg-muted/50 transition-colors flex-shrink-0"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="min-w-0">
            <p className="font-bold text-sm truncate" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              {title || defaultTitle}
            </p>
            <p className="text-xs text-muted-foreground truncate">
              {subtitle || defaultSubtitle}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {routine?.id && (
            <RoutineShareMenu routine={routine}>
              <Button
                variant="ghost"
                size="icon"
                className="h-11 w-11 md:h-8 md:w-8 flex-shrink-0 rounded-full"
                tooltip={t('tooltips.share', 'Share')}
                data-testid="routine-editor-share-btn"
              >
                <Share2 className="h-4 w-4" />
              </Button>
            </RoutineShareMenu>
          )}
          {onShowHistory && routine?.id && (
            <Button
              variant="ghost"
              size="icon"
              className="h-11 w-11 md:h-8 md:w-8 flex-shrink-0 rounded-full"
              tooltip={t('routines.history.button', 'History')}
              onClick={onShowHistory}
              data-testid="routine-editor-history-btn"
            >
              <History className="h-4 w-4" />
            </Button>
          )}
          {onChangeRoutine && (
            <Button
              variant="outline"
              size="sm"
              className="text-xs flex-shrink-0"
              onClick={onChangeRoutine}
              data-testid="change-routine-from-editor"
            >
              <Dumbbell className="mr-1.5 h-3.5 w-3.5" /> {t('routine.change')}
            </Button>
          )}
          <Button variant="ghost" size="icon" className="h-11 w-11 md:h-8 md:w-8 flex-shrink-0 rounded-full hidden sm:flex"
            tooltip={t('common.close')}
            onClick={handleClose}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
      {/* Content — board uses full width; manages its own scroll + rail */}
      <div className="flex-1 min-h-0 overflow-hidden">
        <RoutineEditor
          routine={routine}
          clientUser={clientUser}
          onSave={onSave}
          onSaveAsTemplate={onSaveAsTemplate}
          showNameEditor={showNameEditor}
          onClose={handleClose}
        />
      </div>
    </div>
  );
}
